import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Activity, BookOpen, Database, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const NAV = [
  { to: "/", label: "Visão geral", icon: Activity, id: "overview" },
  { to: "/databases", label: "Bancos", icon: Database, id: "databases" },
  { to: "/backup", label: "Backup", icon: ShieldCheck, id: "backup" },
  { to: "/docs", label: "API", icon: BookOpen, id: "docs" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const isActive = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

  return (
    <div className="min-h-screen md:grid md:grid-cols-[240px_1fr] md:h-screen md:overflow-hidden relative z-10">
      <aside className="hidden md:flex flex-col bg-zinc-950 border-r border-zinc-800" data-testid="sidebar">
        <nav className="flex-1 py-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.id}`}
              className={cn(
                "flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors",
                isActive(n.to)
                  ? "border-orange-600 text-white bg-zinc-900"
                  : "border-transparent text-zinc-400 hover:text-white hover:bg-zinc-900/60"
              )}
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 p-4 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs text-zinc-500">Admin</div>
            <div className="text-sm truncate font-mono" data-testid="sidebar-user-email">{user?.email}</div>
          </div>
          <button
            onClick={() => setConfirmLogout(true)}
            data-testid="logout-button"
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm btn-tactile"
            title="Sair"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>

      <div className="flex flex-col min-h-screen md:h-screen md:overflow-hidden">
        <main className="flex-1 overflow-auto p-4 pb-24 md:p-8 md:pb-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10 grid grid-cols-5"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="bottom-nav"
      >
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`bottom-nav-${n.id}`}
            className={cn(
              "flex flex-col items-center gap-1 py-3 text-[11px] transition-colors",
              isActive(n.to) ? "text-orange-500" : "text-zinc-400"
            )}
          >
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
        <button onClick={() => setConfirmLogout(true)} data-testid="bottom-nav-logout" className="flex flex-col items-center gap-1 py-3 text-[11px] text-zinc-400">
          <LogOut className="h-5 w-5" /> Sair
        </button>
      </nav>

      <ConfirmDialog
        open={confirmLogout}
        onOpenChange={setConfirmLogout}
        title="Sair do console?"
        description="Você será desconectado e precisará entrar novamente para acessar o painel."
        confirmLabel="Sair"
        onConfirm={logout}
        testId="logout-confirm-dialog"
      />
    </div>
  );
}
